import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Heart, X, Star, MapPin, Briefcase, Info, Filter, RefreshCw, Eye, Sparkles, MessageCircle, Flag, Ban, User as UserIcon, Crown, MoreHorizontal, Undo2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import BottomNav from '../components/BottomNav';
import OnlineDot from '../components/OnlineDot';
import ProfileDetailModal from '../components/ProfileDetailModal';
import { useAuth } from '../contexts/AuthContext';
import {
  discoverProfiles, swipeAction, sendHeartbeat, recordProfileView, getNewMembers,
  startConversation, blockUser, swipeUndo
} from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { useKeyboardInset } from '../hooks/useKeyboardInset';

const DiscoverPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const defaultAvatar = useDefaultAvatar();
  const keyboardInset = useKeyboardInset();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showMatch, setShowMatch] = useState(null);
  const [newMembers, setNewMembers] = useState([]);
  const [detailUserId, setDetailUserId] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null); // { message } | null
  const [startingMsg, setStartingMsg] = useState(false);
  const [lastPassedId, setLastPassedId] = useState(null); // track last pass locally for immediate UX
  const [undoing, setUndoing] = useState(false);
  const [introTarget, setIntroTarget] = useState(null); // { profile } — opens intro-note sheet
  const [introText, setIntroText] = useState('');

  useEffect(() => {
    loadProfiles();
    loadNewMembers();
    // Heartbeat every 60 seconds while user is on this page
    sendHeartbeat().catch(() => {});
    const iv = setInterval(() => { sendHeartbeat().catch(() => {}); }, 60000);
    return () => clearInterval(iv);
  }, []);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const response = await discoverProfiles(20);
      setProfiles(response.data);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Failed to load profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNewMembers = async () => {
    try {
      const res = await getNewMembers(10);
      setNewMembers(res.data);
    } catch (e) {
      console.error('Failed to load new members:', e);
    }
  };

  const handleSwipe = async (action) => {
    if (currentIndex >= profiles.length) return;

    const profile = profiles[currentIndex];
    // Fire-and-forget: record a profile view when a user actually engages with a card
    recordProfileView(profile.user_id, 'discover').catch(() => {});
    try {
      const response = await swipeAction(profile.user_id, action);
      if (response.data.match) {
        setShowMatch(profile);
      }
    } catch (error) {
      console.error('Swipe failed:', error);
    }

    if (action === 'pass') {
      setLastPassedId(profile.user_id);
    }

    setCurrentIndex(prev => prev + 1);

    // Load more profiles if running low
    if (currentIndex >= profiles.length - 3) {
      loadProfiles();
    }
  };

  const handleUndo = async () => {
    if (undoing) return;
    setUndoing(true);
    try {
      const res = await swipeUndo();
      const restored = res.data.profile;
      if (restored) {
        // Put the restored profile back at the current index
        setProfiles(prev => {
          const next = prev.slice();
          const insertAt = Math.max(0, currentIndex - 1);
          next.splice(insertAt, 0, restored);
          return next;
        });
        setCurrentIndex(prev => Math.max(0, prev - 1));
      }
      setLastPassedId(null);
    } catch (e) {
      if (e.response?.status === 402) {
        setUpgradePrompt({
          message: e.response.data?.detail ||
            'Undo is a Premium perk. Upgrade to bring back your last pass.'
        });
      } else if (e.response?.status === 404) {
        setLastPassedId(null); // nothing to undo
      } else {
        alert(e.response?.data?.detail || 'Undo failed');
      }
    } finally {
      setUndoing(false);
    }
  };

  const currentProfile = profiles[currentIndex];

  const handleMessage = async (profile, initialMessage = '') => {
    if (!profile || startingMsg) return;
    // Premium users get an "intro note" sheet before starting a brand-new direct convo.
    // Skip the sheet if we already have text (opened from the sheet itself) or user isn't premium.
    if (user?.is_premium && !initialMessage && introTarget?.profile?.user_id !== profile.user_id) {
      setIntroTarget({ profile });
      setIntroText('');
      return;
    }
    setStartingMsg(true);
    try {
      const res = await startConversation(profile.user_id, initialMessage);
      setIntroTarget(null);
      setIntroText('');
      navigate(`/messages/${res.data.match_id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        setIntroTarget(null);
        setUpgradePrompt({
          message: e.response.data?.detail ||
            'Premium members can message anyone. Free members must match first.'
        });
      } else if (e.response?.status === 403) {
        setUpgradePrompt({
          message: e.response.data?.detail || 'You cannot message this member.'
        });
      } else {
        alert(e.response?.data?.detail || 'Failed to open conversation');
      }
    } finally {
      setStartingMsg(false);
    }
  };

  const handleShare = async (profile) => {
    if (!profile) return;
    const name = profile.display_name || profile.name || 'this member';
    const url = `${window.location.origin}/?ref=${profile.user_id}`;
    const shareData = {
      title: `Meet ${name} on GuyanaConnect`,
      text: `Check out ${name}'s profile on GuyanaConnect — connect with singles from Guyana and the diaspora.`,
      url
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${shareData.text} ${url}`);
        alert('Share link copied to clipboard.');
      } else {
        window.prompt('Copy this link:', url);
      }
    } catch (e) {
      // User cancelled or share not available — ignore silently
    }
  };

  const handleBlockCurrent = async () => {
    if (!currentProfile) return;
    if (!window.confirm(`Block ${currentProfile.display_name || currentProfile.name}?`)) return;
    try {
      await blockUser(currentProfile.user_id);
      // Skip to next profile — same as a pass without recording a like
      setCurrentIndex(prev => prev + 1);
    } catch (e) {
      alert('Failed to block');
    }
  };

  if (loading && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#009E60] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20" data-testid="discover-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#009E60] flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" fill="white" />
            </div>
            <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Discover</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/views')} title="Who viewed me" data-testid="discover-nav-views">
              <Eye className="w-5 h-5 text-gray-600" />
            </Button>
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/search')} title="Advanced search" data-testid="discover-nav-search">
              <Filter className="w-5 h-5 text-gray-600" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          {/* New Members Strip */}
          {newMembers.length > 0 && (
            <div className="mb-4" data-testid="new-members-strip">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FFCE00]" />
                  <h3 className="font-semibold text-gray-900 text-sm">New Members</h3>
                </div>
                <button onClick={() => navigate('/search')} className="text-xs text-[#009E60] font-medium" data-testid="see-all-new">See all</button>
              </div>
              <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1">
                {newMembers.map(m => (
                  <button
                    key={m.user_id}
                    onClick={() => { recordProfileView(m.user_id, 'new-strip').catch(() => {}); }}
                    className="flex-shrink-0 flex flex-col items-center gap-1 w-16"
                    data-testid={`new-member-${m.user_id}`}
                  >
                    <div className="relative">
                      <img
                        src={m.photos?.[m.primary_photo_index ?? 0] || m.photos?.[0]}
                        alt={m.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-[#FFCE00]"
                      />
                      {m.is_online && (
                        <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white" />
                      )}
                    </div>
                    <span className="text-[11px] text-gray-700 truncate w-full text-center">{m.display_name || m.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Card Stack */}
          <div className="relative h-[calc(100vh-320px)] min-h-[380px]">
            {currentProfile ? (
              <SwipeCard
                key={currentProfile.user_id}
                profile={currentProfile}
                onSwipe={handleSwipe}
                onOpenProfile={() => setDetailUserId(currentProfile.user_id)}
                onBlock={handleBlockCurrent}
                onShare={() => handleShare(currentProfile)}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-3xl shadow-sm">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <Heart className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  No more profiles
                </h3>
                <p className="text-gray-500 text-center mb-4">
                  Check back later for new matches
                </p>
                <Button onClick={loadProfiles} className="bg-[#009E60] hover:bg-[#007A4B] rounded-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {currentProfile && (
            <>
              {(lastPassedId || user?.is_premium) && (
                <div className="flex justify-center mt-3">
                  <button
                    onClick={handleUndo}
                    disabled={!lastPassedId || undoing}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-sm border border-gray-100 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="undo-last-pass"
                    title={user?.is_premium ? 'Bring back your last pass' : 'Undo is a Premium perk'}
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                    {undoing ? 'Undoing…' : 'Undo last pass'}
                    {!user?.is_premium && <Crown className="w-3 h-3 text-[#FFCE00]" />}
                  </button>
                </div>
              )}
              <div className="flex justify-center items-center gap-3 mt-3" data-testid="action-row">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => handleSwipe('pass')}
                className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-100 hover:border-red-200 transition-colors"
                aria-label="Pass"
                data-testid="swipe-pass"
              >
                <X className="w-7 h-7 text-red-500" />
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => handleSwipe('superlike')}
                className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-100 hover:border-[#FFCE00] transition-colors"
                aria-label="Super like"
                data-testid="swipe-superlike"
              >
                <Star className="w-5 h-5 text-[#FFCE00]" fill="#FFCE00" />
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => handleMessage(currentProfile)}
                disabled={startingMsg}
                className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-100 hover:border-blue-300 transition-colors disabled:opacity-60"
                aria-label="Message"
                data-testid="swipe-message"
              >
                <MessageCircle className="w-7 h-7 text-[#009E60]" />
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => handleSwipe('like')}
                className="w-14 h-14 rounded-full bg-[#009E60] shadow-lg flex items-center justify-center hover:bg-[#007A4B] transition-colors"
                aria-label="Like"
                data-testid="swipe-like"
              >
                <Heart className="w-7 h-7 text-white" fill="white" />
              </motion.button>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Match Modal */}
      <AnimatePresence>
        {showMatch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => setShowMatch(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="match-animation">
                <div className="text-5xl mb-4">🎉</div>
                <h2 className="text-3xl font-bold text-[#009E60] mb-2" style={{ fontFamily: 'Outfit' }}>
                  It's a Match!
                </h2>
                <p className="text-gray-600 mb-6">
                  You and {showMatch.name} liked each other
                </p>

                <div className="flex justify-center gap-4 mb-6">
                  {user?.photos?.[0] && (
                    <img
                      src={user.photos[0]}
                      alt="You"
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  )}
                  {showMatch.photos?.[0] && (
                    <img
                      src={showMatch.photos[0]}
                      alt={showMatch.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-full"
                    onClick={() => setShowMatch(null)}
                  >
                    Keep Swiping
                  </Button>
                  <Button
                    className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-full"
                    onClick={() => navigate('/messages')}
                  >
                    Send Message
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Detail Modal */}
      {detailUserId && (
        <ProfileDetailModal
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
          onMessage={(p) => { setDetailUserId(null); handleMessage(p); }}
          onLike={() => {
            // Route like through existing swipe pipeline for current profile
            if (currentProfile?.user_id === detailUserId) handleSwipe('like');
          }}
          onBlocked={() => { setDetailUserId(null); setCurrentIndex(prev => prev + 1); }}
        />
      )}

      {/* Upgrade prompt for direct messaging */}
      <AnimatePresence>
        {upgradePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => setUpgradePrompt(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 max-w-sm w-full text-center"
              data-testid="upgrade-prompt"
            >
              <div className="w-14 h-14 rounded-full bg-[#FFCE00]/20 flex items-center justify-center mx-auto mb-3">
                <Crown className="w-7 h-7 text-[#FFCE00]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Message anyone with Premium
              </h3>
              <p className="text-gray-600 mb-5 text-sm">
                {upgradePrompt.message}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setUpgradePrompt(null)} className="flex-1 rounded-full" data-testid="upgrade-later">
                  Maybe later
                </Button>
                <Button
                  onClick={() => { setUpgradePrompt(null); navigate('/pricing'); }}
                  className="flex-1 bg-[#FFCE00] hover:bg-yellow-500 text-gray-900 rounded-full font-semibold"
                  data-testid="upgrade-cta"
                >
                  <Crown className="w-4 h-4 mr-1" /> Go Premium
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Intro note sheet — Premium members starting a direct convo */}
      <AnimatePresence>
        {introTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => !startingMsg && setIntroTarget(null)}
            data-testid="intro-note-backdrop"
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 w-full sm:max-w-md shadow-2xl max-h-[90dvh] overflow-y-auto"
              style={{
                // Push the sheet up above the on-screen keyboard on mobile so the Send button stays visible.
                // Desktop / no keyboard = 0, sheet keeps its natural position.
                marginBottom: keyboardInset ? `${keyboardInset}px` : undefined,
                paddingBottom: keyboardInset
                  ? undefined
                  : 'max(1.25rem, env(safe-area-inset-bottom))'
              }}
              data-testid="intro-note-sheet"
            >
              <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-3 sm:hidden" />
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={introTarget.profile?.photos?.[introTarget.profile?.primary_photo_index ?? 0] || introTarget.profile?.photos?.[0] || defaultAvatar}
                  alt={introTarget.profile?.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                    Say hi to {introTarget.profile?.display_name || introTarget.profile?.name}
                  </h3>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Crown className="w-3 h-3 text-[#FFCE00]" /> Premium direct message
                  </p>
                </div>
              </div>
              <Input
                autoFocus
                value={introText}
                onChange={(e) => setIntroText(e.target.value)}
                onFocus={(e) => {
                  // Ensure the Send button stays visible on browsers without visualViewport support
                  setTimeout(() => {
                    e.target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
                  }, 250);
                }}
                placeholder="Break the ice — a friendly note lands better than an empty chat"
                maxLength={280}
                className="rounded-xl h-12"
                data-testid="intro-note-input"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-gray-400">{introText.length}/280 · optional</span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => { setIntroTarget(null); setIntroText(''); }}
                    disabled={startingMsg}
                    data-testid="intro-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleMessage(introTarget.profile, introText.trim())}
                    disabled={startingMsg}
                    className="bg-[#009E60] hover:bg-[#007A4B] rounded-full px-5"
                    data-testid="intro-send"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    {startingMsg ? 'Opening…' : introText.trim() ? 'Send & open chat' : 'Skip & open chat'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

const SwipeCard = ({ profile, onSwipe, onOpenProfile, onBlock, onShare }) => {
  const defaultAvatar = useDefaultAvatar();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const passOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = (event, info) => {
    if (info.offset.x > 100) {
      onSwipe('like');
    } else if (info.offset.x < -100) {
      onSwipe('pass');
    }
  };

  const defaultPhoto = defaultAvatar || 'https://images.unsplash.com/photo-1650702970095-f5b8ebeebce5?w=600';
  const photo = profile.photos?.[profile.primary_photo_index ?? 0] || profile.photos?.[0] || defaultPhoto;

  return (
    <motion.div
      className="absolute inset-0 cursor-grab active:cursor-grabbing swipe-card"
      style={{ x, rotate }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      data-testid="swipe-card"
    >
      <div className="w-full h-full rounded-3xl overflow-hidden shadow-xl relative">
        <img
          src={photo}
          alt={profile.name}
          className="w-full h-full object-cover cursor-pointer"
          draggable={false}
          onClick={onOpenProfile}
          data-testid="view-profile-tap"
        />

        {profile.photo_blurred && (
          <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="blurred-badge">
            <span>🔒</span>
            <span>Photo protected</span>
          </div>
        )}

        {/* Like indicator */}
        <motion.div
          className="like-indicator"
          style={{ opacity: likeOpacity }}
        >
          LIKE
        </motion.div>

        {/* Pass indicator */}
        <motion.div
          className="pass-indicator"
          style={{ opacity: passOpacity }}
        >
          NOPE
        </motion.div>

        {/* Profile info overlay */}
        <div className="profile-overlay">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                  {profile.display_name || profile.name}, {profile.age || '??'}
                </h2>
                {profile.is_online && (
                  <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-white" /> Online
                  </span>
                )}
              </div>
              {(profile.current_city || profile.location) && (
                <div className="flex items-center gap-1 text-white/80 mt-1">
                  <MapPin className="w-4 h-4" />
                  <span>{profile.current_city || profile.location}</span>
                </div>
              )}
              {!profile.is_online && profile.last_active && (
                <div className="text-white/70 text-xs mt-1">
                  <OnlineDot isOnline={false} lastActive={profile.last_active} size={6} />
                </div>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-colors relative z-10"
                  aria-label="More options"
                  data-testid="card-info-btn"
                >
                  <Info className="w-5 h-5 text-white" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpenProfile(); }} data-testid="menu-view-profile">
                  <UserIcon className="w-4 h-4 mr-2" /> View full profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onShare?.(); }} data-testid="menu-share">
                  <Share2 className="w-4 h-4 mr-2" /> Share profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onOpenProfile(); }}
                  data-testid="menu-report"
                  className="text-red-600 focus:text-red-700"
                >
                  <Flag className="w-4 h-4 mr-2" /> Report
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onBlock?.(); }}
                  data-testid="menu-block"
                  className="text-gray-700"
                >
                  <Ban className="w-4 h-4 mr-2" /> Block
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {profile.bio && (
            <p className="text-white/90 mt-3 line-clamp-2">{profile.bio}</p>
          )}

          {profile.interests?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {profile.interests.slice(0, 3).map((interest, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-sm text-white"
                >
                  {interest}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DiscoverPage;
